import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ErrorBox, Loading } from '../components/Feedback';
import { TicketCard } from '../components/TicketCard';
import { api } from '../lib/api';
import { useTickets } from '../lib/ticket-state';
import { MOBILE_TABS, removeTicketFromQueue } from '../lib/workflow';
import { useTicketRefresh } from '../lib/use-ticket-refresh';
import { emptyTicketStream, streamTickets } from '../lib/ticket-stream';
import type { MobileTab } from '../types';

const TAB_KEY = 'nova:tickets:active-tab';
function initialTab(value: string | null): MobileTab { const saved = value || sessionStorage.getItem(TAB_KEY); return saved === 'in_progress' || saved === 'completed' ? saved : 'new'; }
export function TicketsPage() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<MobileTab>(() => initialTab(params.get('tab')));
  const [state, setState] = useState(emptyTicketStream);
  const [loading, setLoading] = useState(true), [error, setError] = useState(''), [changed, setChanged] = useState(false);
  const progress = useRef(emptyTicketStream());
  const controller = useRef<AbortController | null>(null);
  const excludedQueueIds = useRef(new Set<string>());
  const { mergeMany } = useTickets();
  useEffect(() => {
    const removed = (event: Event) => {
      const id = (event as CustomEvent<{id?: string}>).detail?.id;
      if (!id || tab === 'completed') return;
      excludedQueueIds.current.add(id);
      const items = removeTicketFromQueue(progress.current.items, id);
      progress.current = { ...progress.current, items, total: null };
      setState(progress.current); setChanged(true);
    };
    window.addEventListener('nova:ticket-queue-removed', removed);
    return () => window.removeEventListener('nova:ticket-queue-removed', removed);
  }, [tab]);
  const load = useCallback(async (resume = false) => {
    controller.current?.abort();
    const current = new AbortController(); controller.current = current;
    if (!resume) { excludedQueueIds.current.clear(); progress.current = emptyTicketStream(); setState(progress.current); }
    setLoading(true); setError(''); setChanged(false);
    try {
      await streamTickets(page => api.tickets(tab, page, 20, false, current.signal), progress.current, next => {
        if (current.signal.aborted) return;
        const items = tab === 'completed' ? next.items : next.items.filter((ticket) => !excludedQueueIds.current.has(ticket.ticketId || ticket._id));
        const visible = items.length === next.items.length ? next : { ...next, items, total: null };
        progress.current = visible; setState(visible); mergeMany(visible.items);
      }, current.signal);
    } catch (reason) {
      if (!current.signal.aborted) setError(reason instanceof Error ? reason.message : 'Lista nu poate fi încărcată.');
    } finally { if (!current.signal.aborted) setLoading(false); }
  }, [tab, mergeMany]);
  useEffect(() => {
    sessionStorage.setItem(TAB_KEY, tab); setParams({tab}, {replace:true}); void load();
    return () => controller.current?.abort();
  }, [tab, setParams, load]);
  // Do not restart an entire traversal on every live event or focus.
  const notifyChange = useCallback(() => { setChanged(true); }, []);
  useTicketRefresh(notifyChange);
  return <div className="page">
    <header className="simple-header"><h1>Tichete</h1><button className="icon-btn" onClick={() => void load()} aria-label="Reîncarcă">↻</button></header>
    <div className="tabs">{MOBILE_TABS.map(item => <button className={tab === item.value ? 'active' : ''} key={item.value} onClick={() => setTab(item.value)}>{item.label}</button>)}</div>
    <section className="page-body">
      {changed && <div role="status">Lista poate avea actualizări. <button onClick={() => void load()}>Actualizează lista</button></div>}
      <p role="status">Încărcate {state.items.length}{state.total !== null ? ` din ${state.total}` : ''}{loading ? ' · Se încarcă în continuare…' : state.complete ? ' · Lista completă' : ''}</p>
      {loading && !state.items.length && <Loading />}
      {error && <div><ErrorBox message={error} /><button onClick={() => void load(true)}>Reîncearcă</button></div>}
      {state.items.map(ticket => <TicketCard key={`${ticket.ticketId}:${ticket.runId || ''}`} ticket={ticket} />)}
      {!loading && !error && state.complete && !state.items.length && <div className="empty">Nu există tichete în această listă.</div>}
    </section>
  </div>;
}
